import { ImplAPI, Req } from './ImplAPI';

// ============================================================================
// GraphQL Mutations
// ============================================================================
export const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const GET_CART_QUERY = `
  query getCart($cartId: ID!) {
    cart(id: $cartId) {
      id
    }
  }
`;
export const CART_DELIVERY_ADDRESS_MUTATION = `
  mutation cartDeliveryAddressesReplace($cartId: ID!, $deliveryAddresses: [MailingAddressInput!]!) {
    cartDeliveryAddressesReplace(cartId: $cartId, deliveryAddresses: $deliveryAddresses) {
      cart {
        id
        checkoutUrl
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const CART_SELECTED_DELIVERY_OPTIONS_MUTATION = `
  mutation cartSelectedDeliveryOptionsUpdate($cartId: ID!, $selectedDeliveryOptions: [CartSelectedDeliveryOptionInput!]!) {
    cartSelectedDeliveryOptionsUpdate(cartId: $cartId, selectedDeliveryOptions: $selectedDeliveryOptions) {
      cart {
        id
        checkoutUrl
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// ============================================================================
// Types & Interfaces
// ============================================================================

// Generic GraphQL payload structures
export interface GraphQLBody<TVariables> {
  query: string;
  variables: TVariables;
}

export interface GraphQLResponse<TData> {
  data: TData;
  errors?: Array<{ message: string }>; // High-level GraphQL execution errors
}

// Specific input types for the Delivery Address mutation
export interface MailingAddressInput {
  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  province?: string;
  country?: string;
  zip?: string;
  phone?: string;
}

export interface UpdateAddressVariables {
  cartId: string;
  deliveryAddresses: MailingAddressInput[];
}

// Specific input types for the Delivery Options mutation
export interface CartSelectedDeliveryOptionInput {
  deliveryGroupId: string;
  deliveryOptionHandle: string;
}

export interface UpdateDeliveryOptionsVariables {
  cartId: string;
  selectedDeliveryOptions: CartSelectedDeliveryOptionInput[];
}

// ============================================================================
// API Class Implementation
// ============================================================================

export class CartAPI extends ImplAPI {
  // NOTE: If you are proxying these requests through your own backend, use that route.
  // If you are hitting Shopify directly, you may need to override this or adjust `API_BASE`
  // in your environment file to point to: /api/2024-04/graphql.json
  static get route() {
    return '/api/2024-04/graphql.json';
  }

  /**
   * Helper: Create a new cart session and retrieve the Checkout URL
   */
  static async createCart({
    lines,
    customer,
    address,
    recipientName,
    deliveryMethod,
    instructions,
  }: {
    lines: { merchandiseId: string; quantity: number }[];
    customer: {
      phone: string;
    };
    address: MailingAddressInput & { firstName: string; lastName: string };
    recipientName?: string;
    deliveryMethod?: 'SHIPPING' | 'PICK_UP';
    instructions?: string;
  }) {
    // 1. Initialize the CartInput payload
    const input: any = {
      lines,
      attributes: [],
    };
    if (recipientName)
      input.attributes.push({ key: 'Recipient Name', value: recipientName });
    if (instructions)
      input.attributes.push({
        key: 'Delivery Instructions',
        value: instructions,
      }); // 3. Attach User & Address (Buyer Identity)
    if (customer || address) {
      input.attributes.push({
        key: 'Delivery Method',
        value: 'SHIPPING',
      });
      input.buyerIdentity = {
        phone: customer.phone,
      };

      if (address) {
        // Shopify requires addresses to be wrapped in this specific array structure
        input.buyerIdentity.deliveryAddressPreferences = [
          { deliveryAddress: address },
        ];
      }
    }
    return this.mutate<any, { input: any }>(
      CART_CREATE_MUTATION,
      { input },
      {
        'X-Shopify-Storefront-Access-Token': import.meta.env
          .VITE_SHOPIFY_STOREFRONT_PUBLIC_TOKEN,
      },
    );
  }

  /**
   * MUTATION AGNOSTIC RUNNER
   * This handles the generic structure of a GraphQL request. You can pass ANY mutation
   * and variables here, and strict typings will ensure your components get autocomplete.
   */
  static async mutate<TResponseData, TVariables>(
    query: string,
    variables: TVariables,
    customHeaders?: HeadersInit,
  ) {
    return Req<GraphQLResponse<TResponseData>, GraphQLBody<TVariables>>({
      method: 'POST',
      route: this.route,
      body: { query, variables },
      headers: customHeaders,
      uri: import.meta.env.VITE_SHOPIFY_STORE_DOMAIN,
      // Example of custom headers if hitting Shopify directly:
      // headers: { 'X-Shopify-Storefront-Access-Token': 'YOUR_TOKEN', ...customHeaders }
    });
  }

  /**
   * Helper: Replace Cart Delivery Address
   */
  static async updateDeliveryAddress(variables: UpdateAddressVariables) {
    // You can replace 'any' with a strict response interface if you want
    // autocomplete for the returned cart object.
    return this.mutate<any, UpdateAddressVariables>(
      CART_DELIVERY_ADDRESS_MUTATION,
      variables,
      {
        'X-Shopify-Storefront-Access-Token': import.meta.env
          .VITE_SHOPIFY_STOREFRONT_PUBLIC_TOKEN,
      },
    );
  }

  /**
   * Helper: Check if a cart still exists on Shopify.
   * If it returns null, the cart was either purchased or expired.
   */
  static async checkCartStatus(cartId: string) {
    return this.mutate<{ cart: { id: string } | null }, { cartId: string }>(
      GET_CART_QUERY,
      { cartId },
      {
        'X-Shopify-Storefront-Access-Token': import.meta.env
          .VITE_SHOPIFY_STOREFRONT_PUBLIC_TOKEN,
      },
    );
  }

  /**
   * Helper: Update Selected Delivery Options
   */
  static async updateSelectedDeliveryOptions(
    variables: UpdateDeliveryOptionsVariables,
  ) {
    return this.mutate<any, UpdateDeliveryOptionsVariables>(
      CART_SELECTED_DELIVERY_OPTIONS_MUTATION,
      variables,
      {
        'X-Shopify-Storefront-Access-Token': import.meta.env
          .VITE_SHOPIFY_STOREFRONT_PUBLIC_TOKEN,
      },
    );
  }
}
